import { environment } from '@env/environment';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, filter, switchMap, tap } from 'rxjs/operators';
import { User } from '@app/core/business/user/user';
import { UserPreferences } from './user-preferences';
import { ApiService } from '@app/core/service/api-ws.service';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public loginAttemptUsername = '';
  private usingDetailledPanel$ = new BehaviorSubject<boolean>(localStorage.getItem('usingDetailledPanel') === 'true');
  private user$ = new BehaviorSubject<User>(null);
  private userMocks: User[] = [
    {
      code: 'user_1',
      firstname: 'Benjamin',
      lastname: 'Bini',
      email: 'mail.zez@foer.com',
      username: 'benjamin@bini.io',
      phoneNumber: '0450540405',
      addressFirstLine: 'addressFirstLine1',
      addressSecondLine: 'addressSecondLine1',
      mobilePhoneNumber: '0450540405',
      city: 'city1',
      zipCode: 'zipCode1',
      country: 'country1',
      defaultLanguage: 'fr-FR',
      preferences: {
        defaultLanguage: 'fr-FR',
      },
      contactsCompanies: [
        {
          companyCode: 'pn',
          contactCode: 'contact_user1_pn',
        },
        {
          companyCode: 'vf',
          contactCode: 'contact_user1_vf',
        },
      ],
      isEmployee: false,
    }
  ];




  constructor(private readonly apiService: ApiService, private authenticationService: AuthenticationService) {
    this.authenticationService.isConnected$.pipe(
      filter(isConnected => isConnected == true),
      tap(x => {
        let userCode: string = localStorage.getItem('userCode') ? localStorage.getItem('userCode') : this.loginAttemptUsername;
        this.getUserFromApi$(userCode).pipe(tap(user => localStorage.setItem('userCode', user.code))).subscribe();
      })).subscribe();
  }


  public notifyUsingDetailledPanel(usingDetailledPanel: boolean): void {
    localStorage.setItem('usingDetailledPanel', usingDetailledPanel.toString());
    this.usingDetailledPanel$.next(usingDetailledPanel);
  }

  public getUsingDetailledPanel$(): Observable<boolean> { return this.usingDetailledPanel$.asObservable(); }

  public getUsingDetailledPanel(): boolean { return this.usingDetailledPanel$.getValue(); }



  public notifyUser(user: User): void { this.user$.next(user); }

  public getUser$(): Observable<User> {
    return this.user$.asObservable();
  }

  public getUserFromApi$(userCode: string): Observable<User> {
    if (environment.useMock) {
      return of(this.userMocks[0]).pipe(delay(200));
    }
    return this.apiService.getUser$(userCode).pipe(tap((user) => this.user$.next(user)));
  }

  public getUsers$(): Observable<User[]> {
    return this.apiService.getUsers$();
  }



  public getUser(): Observable<User> {
    if (this.user$.getValue() && this.user$.getValue().code === this.getUserFromLocalStorage()) {
      return this.user$;
    } else {
      return this.getUserFromApi$(this.getUserFromLocalStorage());
    }
  }


  public getUserFromLocalStorage(): string {
    return localStorage.getItem('userCode');
  }

  public updateUser(): void {
    if (this.user$.getValue() && this.user$.getValue().code === this.getUserFromLocalStorage()) {
      this.user$.next(this.user$.getValue());
    } else {
      this.getUserFromApi$(this.getUserFromLocalStorage()).subscribe(x => this.user$.next(x));
    }
  }

  public switchUser(userCode: string): Observable<any> {
    return this.apiService.switchUser(userCode).pipe(
      tap(response => {
        localStorage.setItem('token', response.jwt);

        // Test si l'on est déjà impersonnifié
        if (!localStorage.getItem('oldUserCode'))
          localStorage.setItem('oldUserCode', localStorage.getItem('userCode'));

        localStorage.setItem('userCode', userCode);
        this.authenticationService.isConnected$.next(true);
      })
    );
  }

  public putPreferences(
    userCode: string,
    userPreferences: UserPreferences
  ): Observable<any> {
    return this.apiService.putPreferences$(userCode, userPreferences);
  }

  public putPassword(
    userCode: string,
    currentPassword: string,
    newPassword: string,
    repeatNewPassword: string
  ): Observable<any> {
    return this.apiService.putPassword$(userCode, currentPassword, newPassword, repeatNewPassword);
  }
}
